/**
 * Dynamic-block grip writer — DYN-4 Phase 4b / Fix 7.
 *
 * Re-emits a `DynamicGrip` back into the flat DXF OBJECTS record shape
 * AutoCAD consumes. Phase 4b optimises for FIDELITY over reconstruction:
 * when `grip.rawGroups` is non-empty we re-emit the original group list
 * verbatim, because AutoCAD's grip records tangle the `AcDbBlockGrip`
 * envelope with vendor-specific 91/92/93/1071/280/98/99 codes that are
 * not worth reconstructing without a full evaluator model.
 *
 * The schema-driven fallback exists so code that programmatically
 * constructs a `DynamicGrip` (tests / future UI authoring flow) can
 * still produce a valid DXF record with the canonical envelope:
 *
 *     0   BLOCK<KIND>GRIP
 *     5   <handle>
 *   330   <owner>
 *   100   AcDbEvalExpr
 *   100   AcDbBlockElement
 *   100   AcDbBlockGrip
 *  1010 1020 1030  <position>        ← only when `grip.position` is set
 *   330   <parameter handle>          ← only when non-empty
 *   100   AcDbBlock<Kind>Grip
 *
 * This minimal shape is enough for AutoCAD to round-trip the grip; any
 * further payload is only needed when the grip drives live evaluation,
 * which is out of Phase 4b's scope.
 *
 * Paired with `dxfBridge.dynamic.parser.grips.ts` (symmetric decoder).
 */

import type { DxfDynamicBlockObject, DxfGroup } from "@dxf-ai-editor/dxf-io";

import type { DynamicGrip } from "./dynamicBlockGrips";
import { pushGroup } from "./dxfBridge.dynamic.shared";

// ═══════════════════════════════════════════════════════════════════════════
// Kind → class name mapping
// ═══════════════════════════════════════════════════════════════════════════

const CLASS_NAME_BY_KIND: Record<DynamicGrip["kind"], string> = {
  linear: "BLOCKLINEARGRIP",
  visibility: "BLOCKVISIBILITYGRIP",
  flip: "BLOCKFLIPGRIP",
  rotation: "BLOCKROTATIONGRIP",
  alignment: "BLOCKALIGNMENTGRIP",
  "properties-table": "BLOCKPROPERTIESTABLEGRIP",
  user: "BLOCKUSERPARAMETERGRIP",
};

/**
 * AcDbBlock<Kind>Grip subclass marker for each grip kind. These strings
 * appear as the terminal `100` entry on a grip record. They mirror the
 * AutoCAD naming convention (`AcDbBlockLinearGrip`, `AcDbBlockVisibilityGrip`,
 * …). For the properties-table grip, AutoCAD omits the terminal marker in
 * some vintages — we always emit it in the schema-driven path because its
 * presence is harmless and unifies the envelope shape.
 */
const SUBCLASS_MARKER_BY_KIND: Record<DynamicGrip["kind"], string> = {
  linear: "AcDbBlockLinearGrip",
  visibility: "AcDbBlockVisibilityGrip",
  flip: "AcDbBlockFlipGrip",
  rotation: "AcDbBlockRotationGrip",
  alignment: "AcDbBlockAlignmentGrip",
  "properties-table": "AcDbBlockPropertiesTableGrip",
  user: "AcDbBlockUserParameterGrip",
};

// ═══════════════════════════════════════════════════════════════════════════
// Writer entry point
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Emit one DynamicGrip as a DXF OBJECTS record. Verbatim passthrough is
 * preferred when `rawGroups` is populated; otherwise a canonical minimal
 * envelope is synthesised.
 */
export function writeGrip(grip: DynamicGrip): DxfDynamicBlockObject {
  const className = CLASS_NAME_BY_KIND[grip.kind];
  const subclassMarker = SUBCLASS_MARKER_BY_KIND[grip.kind];

  // FIDELITY PATH — re-emit the original group list unchanged. AutoCAD
  // records carry dozens of vendor-specific codes (91/92/93/280/1071/98/99)
  // that we cannot faithfully reconstruct without the full eval graph
  // model. When the parser preserved the original groups, trust them.
  if (grip.rawGroups.length > 0) {
    return {
      kind: "dynamic-block-object",
      type: className,
      handle: grip.id,
      ownerHandle: grip.ownerHandle || undefined,
      rawGroups: grip.rawGroups.slice(),
    };
  }

  // RECONSTRUCTION PATH — synthetic grip (tests / command engine). Emit
  // the minimal canonical envelope.
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, className);
  pushGroup(groups, 5, grip.id);
  if (grip.ownerHandle) pushGroup(groups, 330, grip.ownerHandle);
  pushGroup(groups, 100, "AcDbEvalExpr");
  pushGroup(groups, 100, "AcDbBlockElement");
  pushGroup(groups, 100, "AcDbBlockGrip");
  if (grip.position !== undefined) {
    pushGroup(groups, 1010, grip.position.x);
    pushGroup(groups, 1020, grip.position.y);
    pushGroup(groups, 1030, grip.position.z);
  }
  if (grip.parameterHandle) pushGroup(groups, 330, grip.parameterHandle);
  pushGroup(groups, 100, subclassMarker);

  return {
    kind: "dynamic-block-object",
    type: className,
    handle: grip.id,
    ownerHandle: grip.ownerHandle || undefined,
    rawGroups: groups,
  };
}
